'use client';

import { updatePageParam } from '../SearchResultsFacets/utils';

import { useState } from 'react';
import { useTracking } from 'react-tracking';

import { getKeyValue } from '@/services/content/data';
import { BACK_TO_TOP_LABEL_KEY } from '@/src/containers/main/constants';
import { useSearchContext } from '@/src/services/search/SearchProvider';
import { getIsKeyboardEvent, getIsKeyboardSelect } from '@/src/utils';
import { Box } from '@mui/material';
import {
    BackToTopLink,
    Divider,
    Pagination,
    Skeleton,
} from '@nxt-costco-com/forge-components';
import {
    Analytics,
    type ContentStackEntryDataProps,
    useQueryParams,
} from '@nxt-costco-com/forge-digital-components';

export function SearchResultsPagination({
    pageConfig,
    handleFocus,
}: {
    pageConfig: ContentStackEntryDataProps;
    handleFocus: () => void;
}) {
    const [isKeyboardSelect, setIsKeyboardSelect] = useState(false);
    const { searchResult, isSearchResultsLoading } = useSearchContext();
    const { trackEvent } = useTracking();
    const { queryParams: searchParams, setQueryParams } = useQueryParams();

    /**
     * Function to identify the selected button by checking its aria label
     * @param e
     * @param v
     * @returns
     */
    const getSelectedButton = (e: React.ChangeEvent<unknown>, v: number) => {
        const target = e?.currentTarget as Element;
        return target?.ariaLabel === 'Go to previous page'
            ? 'Previous Arrow'
            : target?.ariaLabel === 'Go to next page'
              ? 'Next Arrow'
              : v.toString();
    };

    const handlePagination = (e: React.ChangeEvent<unknown>, v: number) => {
        trackEvent({
            component: {
                identifier: 'Search',
                trackedData: ['Pagination Control', getSelectedButton(e, v)],
                type: 'nav',
            },
            trackType: 'navigation',
        });
        updatePageParam(setQueryParams, searchParams, v.toString());
        if (isKeyboardSelect) {
            setIsKeyboardSelect(false);
            // Set focus to the first product tile on pagination change
            handleFocus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        // If the pagination event was triggered by a keyboard event,
        // flag that so the pagination handler can set focus on the target element.
        // This is a workaround necessitated by the fact that the pagination event
        // is always a PointerEvent, even when triggered by a keyboard.
        if (getIsKeyboardEvent(e) && getIsKeyboardSelect(e)) {
            setIsKeyboardSelect(true);
            // Clear after 300ms, since the two events should follow one another instantaneously,
            // and there may be subsequent pagination selections.
            setTimeout(() => {
                setIsKeyboardSelect(false);
            }, 300);
        }
    };

    if (isSearchResultsLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    gap: 2,
                }}
            >
                <Skeleton width={35} height={35} />
                <Skeleton width={35} height={35} />
                <Skeleton width={35} height={35} />
            </Box>
        );
    }

    if (!searchResult) {
        return null;
    }

    const { pagination } = searchResult;
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent:
                    searchResult.pagination?.totalPages > 1
                        ? 'space-between'
                        : 'flex-end',
                '@media (max-width: 767px)': {
                    flexDirection: 'column',
                },
                paddingTop: '10px',
            }}
        >
            {pagination?.totalPages > 1 ? (
                <Pagination
                    count={pagination.totalPages}
                    handleChange={handlePagination}
                    handleKeyDown={
                        handleKeyDown as (
                            e: React.KeyboardEvent<Element>,
                        ) => void
                    }
                    page={
                        searchParams.has('currentPage')
                            ? parseInt(searchParams.get('currentPage')!)
                            : 1
                    }
                    defaultPage={1}
                    sx={{ flexGrow: 1 }}
                />
            ) : (
                <Box />
            )}
            <Divider
                sx={{
                    display: 'none',
                    '@media (max-width: 767px)': {
                        display: 'block',
                        marginTop: '10px',
                    },
                }}
            />
            <Box
                sx={{
                    textWrap: 'nowrap',
                    '@media (max-width: 767px)': {
                        alignSelf: 'end',
                        marginTop: '10px',
                    },
                }}
            >
                <Analytics
                    analyticData={{
                        component: {
                            identifier: 'Search',
                            trackedData: ['Back to Top'],
                            type: 'ui',
                        },
                        trackType: 'navigation',
                    }}
                >
                    <BackToTopLink
                        text={getKeyValue(BACK_TO_TOP_LABEL_KEY, pageConfig)}
                    />
                </Analytics>
            </Box>
        </Box>
    );
}
